const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/DashboardPage/Content.tsx';
import * as React from 'react';
import { useEffect, useRef, type JSX } from 'react';
import Grid from '@mui/material/Grid';
import {
    AppointmentCards,
    ECheckinCard,
    HospitalImgCard,
    IdentityProofCard,
    InvoiceCard,
    MedicationCard,
    ResponsibilitiesCard,
    SmallReportsCard,
    SynedraViewerCard,
} from './Cards';
import { usePatientData } from 'App/store/patient';
import { Sidebar } from './Sidebar/Sidebar';
import LoadingSpinner from '@cistec/common_web/components/LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import textMap from 'App/pages/DashboardPage/translations';
import { showSnackbar } from '@cistec/common_web/actions/uiActions';
import { useIntl } from 'react-intl';
import { isSecuredLocationPath } from 'App/utils/urlLocationCheck';
import { getSetupResource } from 'App/utils/clientSetupGetRessource';
import { css } from '@emotion/css';
import { actionLoadIAMUserPatientLink, actionLoadSignedTokenForIAM } from 'App/actions/iam';
import { RegristrationDialogComponent } from './Components/RegristrationDialog/RegristrationDialogComponent';
import DashboardHeaderComponent from 'App/pages/DashboardPage/Components/DashboardHeader/DashboardHeaderComponent';
import { CardSize, DashboardCard, DashboardCardConfiguration, getCardsfromConfig } from './utils/configToCardMapper';
import { loadPatAppRegisterWorkItem } from 'App/actions/workItem';
import { logout } from 'App/actions/fixme.login';
import { NextSurgeryAppointmentCard } from './Cards/NextSurgeryAppointment/NextSurgeryAppointmentCard';
import { useFeatureFlags } from 'App/store/featureFlags';
import { useMetaKatListData } from 'App/store/metaKat';
import {
    invitationCode,
    loginState,
    patRegisterWorkId,
    selectEmailFromRegisterWorkItem,
    selectUserDetailsFromRegisterWorkItem,
} from 'App/store/login';
import { useECheckinRegisteredWorkItem } from 'App/store/workItem';
import { MetaKatAlertList } from 'App/components/Alert/MetaKatAlertList';
import { ResponsiveDialog } from '@cistec/common_web/components/ResponsiveDialog/ResponsiveDialog';
import { CIS4mRoot } from '@cistec/common_web/modules/CIS4m/components/CIS4mRoot';
import { makeSelectCIS4m } from '@cistec/common_web/modules/CIS4m/store';
import { useTheme } from '@mui/material';
import { ECHECKIN_FORM_ID } from 'App/components/eCheckin/actions';
import { useMadeSelector } from '@cistec/common_web/utils/store';
import { BookAppointmentCard } from './Cards/AppointmentCards/BookAppointmentCard';
import { useUserLinked } from 'App/store/userLinkage';
import { selectCanUserBookAppointment } from 'App/store/appointment';
import { getPatNrFromSession } from 'App/utils/sessionStorage';

interface Props {}

const responsiveDialogCSS = css`
    .CIS4mRoot {
        padding: 0;
    }
`;

export const subContainerStyles = css`
    display: block;

    & > * .MuiCard-root {
        margin-bottom: 8px;
    }

    & > *:last-child .MuiCard-root {
        margin-bottom: 0;
    }
`;

export const Spinner = <LoadingSpinner center="absolute" delay={300} />;

export const Content = (props: Props) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<any>();
    const theme = useTheme();

    const invCode = useSelector(invitationCode);
    const { data: isUserLinked } = useUserLinked();

    const { data: featureFlags } = useFeatureFlags();
    const userCanBookAppointment = useSelector(selectCanUserBookAppointment);

    const [patient] = usePatientData();

    const userDetails = useSelector(selectUserDetailsFromRegisterWorkItem);
    const registerWorkId = useSelector(patRegisterWorkId);
    const emailUsedForLogin = useSelector(selectEmailFromRegisterWorkItem);

    const workItemLoginState = useSelector(loginState);

    const cis4mContentRef = useRef<any>(undefined);
    const eCheckinForm = useMadeSelector(() => makeSelectCIS4m(ECHECKIN_FORM_ID));

    // FIXME ... maybe secretly something depends on this somewhere...
    useECheckinRegisteredWorkItem();

    /* FIXME:
    -   this is just bad and makes stuff unclear where it is needed and what it is getting.
    -   move this to the cards where it's actually needed to make the code less messy.
    -   also data could get old and showing wrong things. (i.e. logged in for an hour, then click on medication it will show the medication an hour ago)

    is used in initialValues (eCheckin) to load useSelector(selectRelationTypes) and for AppointmentBooking Countries.
    there may be more but all are related to multiMetaKatKeys inside Store.
    */
    useMetaKatListData();

    const dashboardCardConfig = getSetupResource('dashboardCardConfiguration');
    const cards = getCardsfromConfig(dashboardCardConfig);

    const patNr = getPatNrFromSession();

    const userDetailsFormatted = [userDetails?.get('lastname'), userDetails?.get('firstname')]
        .filter((e) => !!e)
        .join(', ');
    const patientName =
        [patient?.get('NAME'), patient?.get('VORNAME')].filter((e) => !!e).join(', ') || userDetailsFormatted;

    if (workItemLoginState === 'register-error-response' && isSecuredLocationPath()) {
        dispatch(showSnackbar(formatMessage(textMap.workItemFailures)));
        dispatch(logout(3000) as any); // action uses thunk
    }

    useEffect(() => {
        if (invCode) {
            dispatch(actionLoadSignedTokenForIAM());
        }
    }, [invCode]);

    useEffect(() => {
        dispatch(actionLoadIAMUserPatientLink());
    }, []);

    useEffect(() => {
        if (registerWorkId) {
            dispatch(loadPatAppRegisterWorkItem(registerWorkId));
        }
    }, [registerWorkId]);

    const renderCard = (card: DashboardCard) => {
        const { dashboard, appointment } = featureFlags;
        const type = card.CardType;

        switch (type) {
            case 'Medication':
                return dashboard.medication && !!patNr ? <MedicationCard key={type} /> : null;
            case 'Reports':
                return dashboard.report && !!patNr ? <SmallReportsCard key={type} /> : null;
            case 'Invoice':
                return dashboard.invoices && !!patNr ? <InvoiceCard key={type} /> : null;
            case 'SynedraViewer':
                return dashboard.synedraViewer && !!patNr ? <SynedraViewerCard key={type} /> : null;
            case 'Responsibility':
                return appointment.showResponsibilities ? <ResponsibilitiesCard key={type} /> : null;
            case 'ECheckin':
                return dashboard.eCheckin ? <ECheckinCard key={type} /> : null;
            case 'NextAppointment':
                return <AppointmentCards key={type} />;
            case 'NextSurgeryAppointmentCard':
                return dashboard.showSurgeryCard && !!patNr ? <NextSurgeryAppointmentCard key={type} /> : null;
            case 'ExternalLinks':
                return <Sidebar key={type} />;
            case 'IdentityProof':
                return dashboard.showIdentityProof && !!emailUsedForLogin && !isUserLinked ? (
                    <IdentityProofCard key={type} />
                ) : null;
            case 'HospitalImage':
                return dashboard.showHospitalImg && !!emailUsedForLogin && !isUserLinked ? (
                    <HospitalImgCard key={type} />
                ) : null;
            case 'BookAppointmentCard':
                return dashboard.appointmentBooking && userCanBookAppointment ? (
                    <BookAppointmentCard key={type} />
                ) : null;
        }
    };

    const renderCards = (cards: DashboardCardConfiguration, size: CardSize) => {
        return cards[size].Cards.map((card) => renderCard(card));
    };

    const generateGrid = () => {
        return [CardSize.L, CardSize.M, CardSize.S].map((size): JSX.Element => {
            let gridSize = 12;
            switch (size) {
                case CardSize.L:
                    gridSize = 12;
                    break;
                case CardSize.M:
                    gridSize = 8;
                    break;
                case CardSize.S:
                    gridSize = 4;
                    break;
            }
            return (
                <Grid
                    key={`${size}-grid`}
                    container
                    size={{ xs: 12, sm: 12, md: gridSize }}
                    spacing={1}
                    className={subContainerStyles}
                >
                    {renderCards(cards, size)}
                </Grid>
            );
        });
    };

    const renderECheckinDialog = () => {
        if (!eCheckinForm) return null;

        return (
            <ResponsiveDialog
                allowClosing={false}
                className={responsiveDialogCSS}
                sizeSettings={{ width: '840px', height: 'calc(100vh - 40px)' }}
                fullScreen={theme.breakpoints.down('md')}
            >
                <CIS4mRoot ref={cis4mContentRef} fieldVariant={'outlined'} formData={eCheckinForm} />
            </ResponsiveDialog>
        );
    };

    return (
        <>
            <RegristrationDialogComponent />
            <Grid container spacing={1}>
                <MetaKatAlertList />

                <DashboardHeaderComponent patientName={patientName} />
                {generateGrid()}
            </Grid>
            {renderECheckinDialog()}
        </>
    );
};
